import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';
import { useTenantTheme } from '../useTenantTheme.js';
import { RefreshIcon, SaveIcon } from '../components/icons';

/**
 * Per-project help-widget settings.
 *
 * The widget renders the same way on every project unless someone
 * overrides it here.  These knobs ship down via /v1/sdk/config and
 * the widget merges them on top of the host page's init options
 * (host wins on conflict so devs can override locally for testing).
 *
 * Fields map 1:1 onto `HelpWidget._config`:
 *   - disabled     hide the launcher entirely on this project
 *   - mount        'floating' | 'embedded'
 *   - position     'bottom-right' | 'bottom-left'
 *   - label        launcher button text
 *   - quickPrompts pre-canned prompts above the textarea
 */
const POSITIONS = [
  { id: 'bottom-right', label: 'Bottom right' },
  { id: 'bottom-left', label: 'Bottom left' },
];

const MOUNTS = [
  { id: 'floating', label: 'Floating launcher' },
  { id: 'embedded', label: 'Embedded (requires [data-spotlight-help-mount])' },
];

export function ProjectHelp() {
  const { project } = useOutletContext() || {};
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);
  // Pull the tenant's active theme so the preview launcher renders
  // against the same palette real end-users will see in the widget,
  // not a hard-coded indigo placeholder.
  const { style: themeStyle } = useTenantTheme({ projectId: project?.project_id || null });

  // Pull the latest project row in case the outlet context is stale.
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!project?.project_id) return;
    let alive = true;
    api(`/v1/admin/projects/${encodeURIComponent(project.project_id)}`)
      .then((p) => {
        if (!alive) return;
        const cfg = p?.help_config || {};
        // Help is opt-in per project — a brand-new project has no
        // `enabled` key on its config, so the launcher stays hidden
        // until the operator flips this toggle.
        const next = {
          enabled: cfg.enabled === true,
          mount: cfg.mount || 'floating',
          position: cfg.position || 'bottom-right',
          label: cfg.label || 'Need help?',
          quickPrompts: Array.isArray(cfg.quickPrompts) ? cfg.quickPrompts.join('\n')
            : "How do I…\nThis doesn't work\nSuggestion",
        };
        setDraft(next);
        setOriginal(next);
      })
      .catch((e) => setErr(e.message))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, project?.project_id]);

  const dirty = useMemo(() => {
    if (!draft || !original) return false;
    return JSON.stringify(draft) !== JSON.stringify(original);
  }, [draft, original]);

  const save = useCallback(async () => {
    if (!project?.project_id || !draft) return;
    setSaving(true);
    setErr(null);
    try {
      const help_config = {
        enabled: !!draft.enabled,
        mount: draft.mount,
        position: draft.position,
        label: (draft.label || '').trim() || 'Need help?',
        quickPrompts: (draft.quickPrompts || '')
          .split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 6),
      };
      await api(`/v1/admin/projects/${encodeURIComponent(project.project_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ help_config }),
      });
      setOriginal(draft);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }, [api, project?.project_id, draft]);

  const reset = useCallback(() => {
    setDraft({
      enabled: false,
      mount: 'floating',
      position: 'bottom-right',
      label: 'Need help?',
      quickPrompts: "How do I…\nThis doesn't work\nSuggestion",
    });
  }, []);

  const actions = useMemo(() => {
    if (!draft) return [];
    const out = [];
    if (!draft.enabled) {
      out.push({
        id: 'disabled',
        title: 'The help widget is off on this project',
        body: 'New projects start with help disabled — flip the toggle below to show the launcher to your users.',
        cta: 'Turn it on',
        onClick: () => setDraft((d) => ({ ...d, enabled: true })),
        tone: 'primary',
      });
    }
    return out;
  }, [draft]);

  if (loading || !draft) {
    return (
      <div className="state"><div className="state__title">Loading help settings…</div></div>
    );
  }

  return (
    <div className="phelp">
      <ContextActions actions={actions} />

      <div className="phelp__grid">
        <div className="phelp__form">
          <h3 className="card__title">Help widget on {project?.name || 'this project'}</h3>
          <p className="phelp__hint">
            These settings ship down through <code>/v1/sdk/config</code> and the
            widget applies them automatically — no code change needed on the
            host page. Host-page <code>Spotlight.init({'{'} help: … {'}'})</code>
            still wins if a dev hardcodes options.
          </p>

          <label className="phelp__field">
            <span className="phelp__label">Help widget</span>
            <label className="phelp__check">
              <input
                type="checkbox"
                checked={!!draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />
              <span>Show the launcher to users on this project.</span>
            </label>
            <small className="phelp__hint">
              Off by default — turn it on once you're ready for end-user
              help requests on this project to land in the inbox.
            </small>
          </label>

          <label className="phelp__field">
            <span className="phelp__label">Mount style</span>
            <select
              className="select"
              value={draft.mount}
              onChange={(e) => setDraft({ ...draft, mount: e.target.value })}
            >
              {MOUNTS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>

          {draft.mount === 'floating' && (
            <label className="phelp__field">
              <span className="phelp__label">Position</span>
              <select
                className="select"
                value={draft.position}
                onChange={(e) => setDraft({ ...draft, position: e.target.value })}
              >
                {POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </label>
          )}

          <label className="phelp__field">
            <span className="phelp__label">Launcher label</span>
            <input
              type="text"
              className="input"
              maxLength={40}
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Need help?"
            />
            <small className="phelp__hint">Short text on the floating button. e.g. "Support", "Chat with us".</small>
          </label>

          <label className="phelp__field">
            <span className="phelp__label">Quick prompts</span>
            <textarea
              className="textarea"
              rows={4}
              value={draft.quickPrompts}
              onChange={(e) => setDraft({ ...draft, quickPrompts: e.target.value })}
              placeholder={"How do I…\nThis doesn't work\nSuggestion"}
            />
            <small className="phelp__hint">
              One per line. Up to 6. Click in the composer prefills the textarea.
            </small>
          </label>

          {err && <div className="state state--error" style={{ marginTop: 12 }}>{err}</div>}

          <div className="phelp__actions">
            <button type="button" className="btn btn--ghost" onClick={reset} disabled={saving}>
              <RefreshIcon /> Reset to defaults
            </button>
            <button type="button" className="btn btn--primary" onClick={save} disabled={!dirty || saving}>
              <SaveIcon /> {saving ? 'Saving…' : 'Save changes'}
            </button>
            {savedAt && !dirty && (
              <span className="phelp__saved">Saved {savedAt.toLocaleTimeString()}</span>
            )}
          </div>
        </div>

        <div className="phelp__preview" style={themeStyle}>
          <h4>Preview</h4>
          <div className="phelp__preview-stage">
            {!draft.enabled ? (
              <div className="phelp__preview-disabled">
                Widget is off — users won't see anything on this project until you enable it.
              </div>
            ) : (
              <button type="button" className={'phelp__preview-launcher phelp__preview-launcher--' + draft.position}>
                <span className="phelp__preview-launcher-icon">?</span>
                <span>{draft.label || 'Need help?'}</span>
              </button>
            )}
          </div>
          <p className="phelp__hint" style={{ marginTop: 14 }}>
            Live preview is approximate. Open <a href="/help/articles">Help articles</a>
            {' '}to curate the quick-answers that appear above the composer.
          </p>
        </div>
      </div>

      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
  .phelp__grid { display: grid; grid-template-columns: minmax(280px, 1fr) 320px; gap: 24px; align-items: start; }
  @media (max-width: 880px) { .phelp__grid { grid-template-columns: 1fr; } }
  .phelp__form { background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 12px; padding: 18px; }
  .phelp__field { display: flex; flex-direction: column; gap: 6px; margin-top: 14px; }
  .phelp__label { font-size: 12.5px; font-weight: 600; color: var(--text-1, #0f172a); }
  .phelp__hint  { font-size: 12px; color: var(--text-3, #64748b); margin: 0; line-height: 1.5; }
  .phelp__check { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text-2, #334155); }
  .phelp__actions { display: flex; align-items: center; gap: 10px; margin-top: 18px; padding-top: 16px; border-top: 1px solid var(--border-1, #e5e7eb); }
  .phelp__saved { color: var(--text-3, #64748b); font-size: 12px; }
  .textarea { font: inherit; font-size: 13px; padding: 8px 10px; border: 1px solid var(--border-1, #cbd5e1); border-radius: 6px; resize: vertical; min-height: 88px; }

  .phelp__preview { background: var(--surface-1, #f8fafc); border: 1px dashed var(--border-1, #e5e7eb); border-radius: 12px; padding: 18px; }
  .phelp__preview h4 { margin: 0 0 12px; font-size: 13px; font-weight: 600; color: var(--text-2, #334155); text-transform: uppercase; letter-spacing: 0.05em; }
  .phelp__preview-stage { position: relative; height: 160px; background: repeating-linear-gradient(45deg, rgba(15,23,42,0.02), rgba(15,23,42,0.02) 10px, transparent 10px, transparent 20px); border-radius: 8px; overflow: hidden; }
  .phelp__preview-launcher {
    position: absolute; bottom: 14px;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 0 14px 0 8px; height: 40px;
    /* Use the tenant's active --spot-primary so the preview swatch
       matches what end-users actually see.  Falls back to indigo
       when the theme hasn't loaded yet. */
    background: linear-gradient(135deg, var(--spot-primary, #4f46e5), var(--spot-primary-hover, #4338ca));
    color: var(--spot-primary-text, #fff); border-radius: 9999px; border: 0; cursor: default;
    box-shadow: 0 10px 24px -10px rgba(15, 23, 42, 0.35);
    font: inherit; font-size: 13px; font-weight: 600;
  }
  .phelp__preview-launcher--bottom-right { right: 14px; }
  .phelp__preview-launcher--bottom-left  { left: 14px; }
  .phelp__preview-launcher-icon { width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; background: rgba(255,255,255,0.18); font-size: 13px; font-weight: 700; }
  .phelp__preview-disabled { display: flex; align-items: center; justify-content: center; height: 100%; padding: 12px; text-align: center; font-size: 13px; color: var(--text-3, #64748b); }
`;
